<script>
import IconButton from "@/components/shared/IconButton.vue";
import { canCreateLoanable } from "@/helpers/permissions/loanables";
import Vue from "vue";
import Bike from "@/assets/svg/bike.svg";
import Car from "@/assets/svg/car.svg";
import CarTrailer from "@/assets/svg/car_trailer.svg";
import Trailer from "@/assets/svg/trailer.svg";
import { userIsApproved } from "@/helpers/permissions/users";

export default Vue.extend({
  name: "LoanableTypeSelection",
  components: {
    IconButton,
    "svg-bike": Bike,
    "svg-car": Car,
    "svg-car-trailer": CarTrailer,
    "svg-trailer": Trailer,
  },
  props: {
    loanable: {
      type: Object,
      required: true,
    },
    availableTypes: {
      type: Array,
      required: true,
    },
    ownerUser: {
      type: Object,
      required: true,
    },
  },
  computed: {
    allTypesAllowed() {
      return (
        this.availableTypes.includes("car") &&
        this.availableTypes.includes("trailer") &&
        this.availableTypes.includes("bike") &&
        this.availableTypes.includes("car_trailer")
      );
    },
    canCreateLoanable() {
      return canCreateLoanable(this.user) || userIsApproved(this.user);
    },
    communitiesWithChatGroup() {
      return this.ownerUserCommunities.filter((c) => !!c.chat_group_url);
    },
    isCreatorOwner() {
      return this.user.id === this.ownerUser.id;
    },
    ownerUserCanCreateLoanable() {
      return (
        Object.keys(this.ownerUser).length > 0 &&
        (canCreateLoanable(this.ownerUser) || userIsApproved(this.ownerUser))
      );
    },
    ownerUserCommunities() {
      return (this.ownerUser?.accessible_communities ?? []).map((c) => c.community);
    },
    user() {
      return this.$store.state.user;
    },
  },
});
</script>

<template>
  <div class="loanable-types-selection">
    <div v-if="!canCreateLoanable">
      {{ $t("components.loanable.loanable_type_selection.not_approved_message") }}
    </div>
    <div v-else-if="!isCreatorOwner && !ownerUserCanCreateLoanable">
      {{ $t("components.loanable.loanable_type_selection.owner_user_not_approved_message") }}
    </div>
    <div v-else>
      <h3 class="mb-4">
        {{ $t("components.loanable.loanable_type_selection.type_selection_title") }}
      </h3>

      <div class="loanable-types-selection__icons">
        <icon-button
          variant="outline-success"
          :disabled="!availableTypes.includes('car')"
          @click="() => (loanable.type = 'car')"
        >
          <svg-car />
          <div>
            {{ $t("loanables.fields.types.car") }}
            <span v-if="!availableTypes.includes('car')">*</span>
          </div>
        </icon-button>

        <icon-button
          variant="outline-success"
          :disabled="!availableTypes.includes('bike')"
          @click="() => (loanable.type = 'bike')"
        >
          <svg-bike />
          <div>
            {{ $t("loanables.fields.types.bike") }}
            <span v-if="!availableTypes.includes('bike')">*</span>
          </div>
        </icon-button>

        <icon-button
          variant="outline-success"
          :disabled="!availableTypes.includes('trailer')"
          @click="() => (loanable.type = 'trailer')"
        >
          <svg-trailer />
          <div>
            {{ $t("loanables.fields.types.trailer") }}
            <span v-if="!availableTypes.includes('trailer')">*</span>
          </div>
        </icon-button>

        <icon-button
          variant="outline-success"
          :disabled="!availableTypes.includes('car_trailer')"
          @click="() => (loanable.type = 'car_trailer')"
        >
          <svg-car-trailer />
          <div>
            {{ $t("loanables.fields.types.car_trailer") }}
            <span v-if="!availableTypes.includes('car_trailer')">*</span>
          </div>
        </icon-button>
      </div>

      <div v-if="!allTypesAllowed" class="mt-4">
        <div v-if="ownerUserCommunities.length > 1">
          <template v-if="isCreatorOwner">
            {{ $t("components.loanable.loanable_type_selection.not_allowed_message_owner") }}
            <div v-if="communitiesWithChatGroup.length > 0" class="mt-2">
              <strong>{{
                $t("components.loanable.loanable_type_selection.discussion_groups")
              }}</strong>
              <ul>
                <li v-for="c in communitiesWithChatGroup" :key="c.id">
                  <a :href="c.chat_group_url" target="_blank">{{ c.name }}</a>
                </li>
              </ul>
            </div>
          </template>
          <template v-else>
            {{
              $t("components.loanable.loanable_type_selection.not_allowed_message_other", {
                name: ownerUser.full_name,
              })
            }}
          </template>
        </div>
        <div v-else-if="ownerUserCommunities.length === 1">
          {{
            $t("components.loanable.loanable_type_selection.not_allowed_message_single", {
              name: ownerUserCommunities[0].name,
            })
          }}
          <template v-if="isCreatorOwner">
            <a
              v-if="ownerUserCommunities[0].chat_group_url"
              :href="ownerUserCommunities[0].chat_group_url"
              target="_blank"
            >
              {{ $t("components.loanable.loanable_type_selection.get_involved") }}
            </a>
            <span v-else>{{ $t("components.loanable.loanable_type_selection.get_involved") }}</span>
            {{ $t("components.loanable.loanable_type_selection.get_involved_suffix") }}
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.loanable-types-selection {
  &__icons {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    justify-content: center;

    .icon-button {
      border-width: 3px;
      flex-direction: column;
      border-radius: 0.5rem;

      svg {
        fill: currentColor;
        min-width: 5rem;
      }
    }
  }
}
</style>
